import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useDeviceType, { MOBILE_SCREEN } from '@travel/traveler-core/hooks/useDeviceType';
import { toSEOSearchString } from '@travel/traveler-core/utils/seo';
import { isNotEmptyArray } from '@travel/utils';

import Breadcrumb from 'components/Breadcrumb';
import OtherAreas from 'components/OtherAreas';
import type { SearchFormRef } from 'components/SearchForm/types';
import { getRequestBody } from 'pages/ProviderListPage/resolver';

import { pushLocation } from 'store/__router/actions';
import { getIsSSR } from 'store/__router/selectors';
import { getAreaRelatedItems, getAreaRelatedItemsTitle } from 'store/areaDetails/selectors';
import { getLimitationsForCurrentMarket } from 'store/markets/selectors';
import { fetchProviderFilters, updateMapExpansionState } from 'store/providerList/actions';
import {
  getFilters,
  getIsFetchingList,
  getIsMapExpanded,
  getPlaceLayers,
  getSelectedObjectivesFeatureIdMapping,
} from 'store/providerList/selectors';
import { getCouponDetails } from 'store/salesPromotion/selectors';

import { DEFAULT_SORT_KEY, EMPTY_ARRAY } from 'constants/defaultValue';
import type { SortKeysType } from 'constants/provider';
import { FILTER_GROUP_IDS } from 'constants/provider';
import { Translate } from 'core/translate';
import paths from 'core/universalRouter/paths';
import useProviderFeatureFilter from 'hooks/useProviderFeatureFilter';
import type { SearchFormQuery } from 'ProviderList-Types';

import usePageLoaderOnInitial from '../../hooks/usePageLoaderOnInitial';
import type { PriceRangeType } from './components/FilterContainer';
import { toggleFilterOption } from './components/FilterContainer';
import FiltersAndList from './components/FiltersAndList';
import ListAndMap from './components/ListAndMap';
import { MapProvider } from './components/Map/mapContext';
import SearchBar from './components/SearchBar';
import { FIRST_PAGE_PARAMS } from './resolver';
import useClearFilterButton from './useClearFilterButton';

import styles from './providerListPage.module.scss';

type Props = {
  query: SearchFormQuery;
  params?: { pageIdentifier: string };
};

type ToggleOptionsType = {
  features?: string;
  maxPrice?: string;
  minPrice?: string;
  freeCancellation?: string;
  sortKey?: SortKeysType;
  'starRatings[]'?: string[];
  'reviewScores[]'?: string[];
};

function ProviderListPage({ params = { pageIdentifier: '' }, ...props }: Props) {
  const { query } = props;

  const dispatch = useDispatch();
  const isMobile = useDeviceType() === MOBILE_SCREEN;
  const placeLayers = useSelector(getPlaceLayers);
  const isExpandedMap = useSelector(getIsMapExpanded);
  const objectiveFeatureMappingIds = useSelector(getSelectedObjectivesFeatureIdMapping);
  const filters = useSelector(getFilters);
  const couponId = props.query?.salesPromotionId;
  const couponDetail = useSelector(getCouponDetails(couponId));
  const areaRelatedItems = useSelector(getAreaRelatedItems);
  const areaRelatedItemsTitle = useSelector(getAreaRelatedItemsTitle);
  const { features, setFeatures } = useProviderFeatureFilter(
    objectiveFeatureMappingIds,
    query.features,
  );
  const isSSR = useSelector(getIsSSR);
  const limitations = useSelector(getLimitationsForCurrentMarket);

  useEffect(() => {
    dispatch(
      fetchProviderFilters(
        getRequestBody(
          {
            params: params,
            query: query,
          },
          couponDetail,
        ),
      ),
    );
  }, [couponDetail, dispatch, params, query]);

  const searchFreeCancellation = query.freeCancellation === 'true';
  const searchMaxPrice = query.maxPrice ? Number(query.maxPrice) : null;
  const searchMinPrice = query.minPrice ? Number(query.minPrice) : null;
  const searchStarRating =
    (typeof query['starRatings[]'] === 'string'
      ? [query['starRatings[]']]
      : query['starRatings[]']) || EMPTY_ARRAY;
  const searchReviewRating =
    (typeof query['reviewScores[]'] === 'string'
      ? [query['reviewScores[]']]
      : query['reviewScores[]']) || EMPTY_ARRAY;

  const [isClearFilterButtonDisabled, setIsClearFilterButtonDisabled] = useState<boolean>(false);
  const [maxPrice, setMaxPrice] = useState<PriceRangeType>(searchMaxPrice);
  const [minPrice, setMinPrice] = useState<PriceRangeType>(searchMinPrice);
  const [starRating, setStarRating] = useState<Array<string>>(searchStarRating);
  const [reviewRating, setReviewRating] = useState<Array<string>>(searchReviewRating);
  const [freeCancellation, setFreeCancellation] = useState(searchFreeCancellation);
  const [sortKey, setSortKey] = useState(props.query?.sortKey || DEFAULT_SORT_KEY);
  const [newQuery, setNewQuery] = useState(query);

  const searchFormRef = useRef<SearchFormRef>(null);
  const recentPriceFilter = filters.find((filter) => filter.groupId === FILTER_GROUP_IDS.PRICE);
  const appliedMin = minPrice ?? recentPriceFilter?.min;
  const appliedMax = maxPrice ?? recentPriceFilter?.max;

  useClearFilterButton({
    features,
    freeCancellation,
    isClearFilterButtonDisabled,
    maxPrice,
    minPrice,
    recentPriceFilter,
    reviewRating,
    setIsClearFilterButtonDisabled,
    sortKey,
    starRating,
  });

  const onRemoveCoupon = useCallback(() => {
    dispatch(
      pushLocation({
        pathname: paths.providerList.pathResolver(params.pageIdentifier),
        search: toSEOSearchString(
          {
            ...query,
            salesPromotionId: undefined,
            isCouponSpecified: undefined,
            // reset page state
            ...FIRST_PAGE_PARAMS,
          },
          limitations,
        ),
      }),
    );
  }, [dispatch, params.pageIdentifier, query]);

  const toggleFeatureFilterOptions = useCallback(
    (newFeatures: string[]) => {
      let nextFeatures = [...features];

      if (isNotEmptyArray(newFeatures)) {
        const filteredFeatures = features.filter((feature) => !newFeatures?.includes(feature));
        const newlyAddedFeatures = newFeatures.filter((feature) => !features?.includes(feature));

        nextFeatures = [...filteredFeatures, ...newlyAddedFeatures];
      }

      setFeatures(nextFeatures);
      return nextFeatures;
    },
    [features, setFeatures],
  );

  const onFilterChange = (toggleOptions: ToggleOptionsType) => {
    const newQuery = {
      ...query,
      // faceting endpoint won't use min-max price anymore due to requirement change
      minPrice: '',
      maxPrice: '',
      features: features?.toString(),
      sortKey: sortKey,
      'starRatings[]': starRating,
      'reviewScores[]': reviewRating,
      freeCancellation: freeCancellation.toString(),
      ...toggleOptions,
    };

    dispatch(
      fetchProviderFilters(
        getRequestBody(
          {
            params: params,
            query: newQuery,
          },
          couponDetail,
        ),
      ),
    );
    setNewQuery(newQuery);
  };

  const onPriceRangeChanged = (min?: number, max?: number) => {
    if (Number(min) < 0) {
      return;
    }

    setMinPrice(min);
    setMaxPrice(max);
  };

  const onCheckboxChanged = (filterGroupId: string, toggleOption: string) => {
    switch (filterGroupId) {
      case FILTER_GROUP_IDS.SORT: {
        setSortKey(toggleOption as SortKeysType);
        onFilterChange({ sortKey: toggleOption as SortKeysType });
        break;
      }
      case FILTER_GROUP_IDS.RATING: {
        const nextRatings: Array<string> = toggleFilterOption(starRating, toggleOption);
        setStarRating(nextRatings);
        onFilterChange({ 'starRatings[]': nextRatings });
        break;
      }
      case FILTER_GROUP_IDS.REVIEW_SCORE: {
        const nextReviewScores: Array<string> = toggleFilterOption(reviewRating, toggleOption);
        setReviewRating(nextReviewScores);
        onFilterChange({ 'reviewScores[]': nextReviewScores });
        break;
      }
      case FILTER_GROUP_IDS.CANCELLATION_POLICY: {
        setFreeCancellation(!freeCancellation);
        onFilterChange?.({ freeCancellation: String(!freeCancellation) });
        break;
      }
      default: {
        const nextFeatures = toggleFeatureFilterOptions([toggleOption]);
        onFilterChange?.({ features: nextFeatures.toString() });
        break;
      }
    }
  };

  const onClearClick = () => {
    setMinPrice(null);
    setMaxPrice(null);
    setFeatures(EMPTY_ARRAY);
    setStarRating(EMPTY_ARRAY);
    setReviewRating(EMPTY_ARRAY);
    setSortKey(DEFAULT_SORT_KEY);
    setFreeCancellation(false);

    onFilterChange({
      minPrice: '',
      maxPrice: '',
      features: '',
      sortKey: DEFAULT_SORT_KEY,
      'starRatings[]': EMPTY_ARRAY,
      'reviewScores[]': EMPTY_ARRAY,
      freeCancellation: 'false',
    });

    setIsClearFilterButtonDisabled(true);
  };

  const onSearch = () => {
    searchFormRef.current?.onSubmit();
  };

  const onUpdateListView = useCallback(() => {
    dispatch(updateMapExpansionState(!isExpandedMap));
  }, [dispatch, isExpandedMap]);

  // page loader
  const isFetchingList = useSelector(getIsFetchingList);
  usePageLoaderOnInitial('providerList', isFetchingList);

  return (
    <MapProvider>
      <SearchBar
        className={styles.searchFormWrapper}
        searchFormRef={searchFormRef}
        query={query}
        pageIdentifier={params.pageIdentifier}
        shouldOpenDatePickerOnMount={isSSR}
        onRemoveCoupon={onRemoveCoupon}
        setSortKey={setSortKey}
        filterFormProps={
          !isMobile
            ? { maxPrice, minPrice, starRating, reviewRating, sortKey, features, freeCancellation }
            : {}
        }
        onSearchFilter={onSearch}
      />
      <div className={styles.contentWrapper} data-testid="providerListPage-content-wrapper">
        {!isMobile ? (
          <FiltersAndList
            filtersInListProps={{
              isClearFilterButtonDisabled,
              setMaxPrice,
              setMinPrice,
              setStarRating,
              setReviewRating,
              onCheckboxChanged,
              onPriceRangeChanged,
              onClearClick,
              onSearch,
            }}
            filterFormProps={{
              starRating,
              reviewRating,
              sortKey,
              features,
              freeCancellation,
              maxPrice: appliedMax,
              minPrice: appliedMin,
            }}
            onUpdateListView={onUpdateListView}
            isExpandedMap={isExpandedMap}
            query={query}
            params={params}
            onRemoveCoupon={onRemoveCoupon}
            newQuery={newQuery}
          />
        ) : (
          <ListAndMap
            isExpandedMap={isExpandedMap}
            query={query}
            params={params}
            onRemoveCoupon={onRemoveCoupon}
          />
        )}

        {!isMobile && (
          <>
            {isNotEmptyArray(areaRelatedItems.trainStations) && (
              <OtherAreas
                title={
                  <Translate
                    id="Provider_List.Train_Stations.Title"
                    data={{
                      city_name: areaRelatedItemsTitle,
                    }}
                  />
                }
                type="TRAINSTATIONS"
                otherAreas={areaRelatedItems.trainStations}
                wrapperClassName={styles.otherAreasWrapper}
              />
            )}

            {isNotEmptyArray(areaRelatedItems.pointsOfInterest) && (
              <OtherAreas
                title={
                  <Translate
                    id="Provider_List.Points_Of_Interest.Title"
                    data={{
                      city_name: areaRelatedItemsTitle,
                    }}
                  />
                }
                type="POINTSOFINTEREST"
                otherAreas={areaRelatedItems.pointsOfInterest}
                wrapperClassName={styles.otherAreasWrapper}
              />
            )}

            {isNotEmptyArray(areaRelatedItems.hotSprings) && (
              <OtherAreas
                title={
                  <Translate
                    id="Provider_List.Hot_Springs.Title"
                    data={{
                      subdivision_name: areaRelatedItemsTitle,
                    }}
                  />
                }
                type="HOTSPRINGS"
                otherAreas={areaRelatedItems.hotSprings}
                wrapperClassName={styles.otherAreasWrapper}
              />
            )}
          </>
        )}

        {!isMobile && <Breadcrumb className={styles.breadcrumb} placeLayers={placeLayers} />}
      </div>
    </MapProvider>
  );
}

export default ProviderListPage;
